'use client';

import { c as _c } from "react/compiler-runtime";
import { type RefObject, useEffect, useRef, useState } from 'react';
import { isAutoplayBlockedError } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/autoplay';

// How often to re-attempt a play that has not taken. Slow enough to be
// free, fast enough that nobody sees it.
const RETRY_MS = 250;
interface VideoPlaybackOptions {
  videoRef: RefObject<HTMLVideoElement | null>;
  // Everything the slide needs before the clip should start. Each caller
  // composes its own; this hook only obeys it.
  shouldPlay: boolean;
  // Bumped to restart from the top. Slides are keyed per visit, so this is
  // really only for a lab scrub.
  restartKey?: number;
  // play() was refused for good -- iOS Low Power Mode, an autoplay policy.
  // Retrying stops, but the hook keeps listening: once the viewer starts
  // the clip by hand (see playVideoFromGesture) it reports it as playing.
  onPlayBlocked?: () => void;
}
export const useVideoPlayback = t0 => {
  const $ = _c(9);
  const {
    videoRef,
    shouldPlay,
    restartKey: t1,
    onPlayBlocked
  } = t0;
  const restartKey = t1 === undefined ? 0 : t1;
  const [isPlaying, setIsPlaying] = useState(false);
  const onPlayBlockedRef = useRef(onPlayBlocked);
  let t2;
  if ($[0] !== onPlayBlocked) {
    t2 = () => {
      onPlayBlockedRef.current = onPlayBlocked;
    };
    $[0] = onPlayBlocked;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  useEffect(t2);
  let t3;
  if ($[2] !== shouldPlay || $[3] !== videoRef) {
    t3 = () => {
      const element = videoRef.current;
      if (!element) {
        return;
      }
      if (!shouldPlay) {
        if (!element.paused) {
          element.pause();
        }
        setIsPlaying(false);
        return;
      }
      let cancelled = false;
      let isBlocked = false;
      let timer = 0;
      const stopRetrying = () => {
        if (timer) {
          window.clearInterval(timer);
          timer = 0;
        }
      };
      const attempt = () => {
        if (cancelled || element.seeking) {
          return;
        }
        if (!element.paused) {
          stopRetrying();
          setIsPlaying(true);
          return;
        }
        if (isBlocked) {
          return;
        }
        element.muted = true;
        element.play().then(() => {
          if (cancelled) {
            return;
          }
          stopRetrying();
          setIsPlaying(true);
        }).catch(error => {
          if (cancelled || isBlocked || !isAutoplayBlockedError(error)) {
            return;
          }
          isBlocked = true;
          stopRetrying();
          onPlayBlockedRef.current?.();
        });
      };
      if (element.currentTime > 0) {
        element.currentTime = 0;
      }
      attempt();
      element.addEventListener("canplay", attempt);
      element.addEventListener("loadeddata", attempt);
      element.addEventListener("seeked", attempt);
      element.addEventListener("playing", attempt);
      timer = window.setInterval(attempt, RETRY_MS);
      return () => {
        cancelled = true;
        stopRetrying();
        element.removeEventListener("canplay", attempt);
        element.removeEventListener("loadeddata", attempt);
        element.removeEventListener("seeked", attempt);
        element.removeEventListener("playing", attempt);
      };
    };
    $[2] = shouldPlay;
    $[3] = videoRef;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== restartKey || $[6] !== shouldPlay || $[7] !== videoRef) {
    t4 = [videoRef, shouldPlay, restartKey];
    $[5] = restartKey;
    $[6] = shouldPlay;
    $[7] = videoRef;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  useEffect(t3, t4);
  return isPlaying;
};